import { useState } from 'react'
import { DndContext, DragOverlay, useDraggable, useDroppable, pointerWithin } from '@dnd-kit/core'
import { calendarApi } from '../services/api'
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, CalendarIcon } from './Icons'
import { useToast } from './ToastContainer'
import '../styles/CalendarView.css'

// Draggable milestone/task item
function DraggableItem({ id, type, data, planTitle }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${type}-${id}`,
    data: { type, ...data, planTitle }
  })

  const priorityColors = {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#22c55e'
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`draggable-item ${type} ${isDragging ? 'dragging' : ''}`}
      style={{
        borderLeftColor: type === 'task' ? priorityColors[data.priority] || '#3b82f6' : '#8b5cf6'
      }}
    >
      <span className="item-title">{data.title}</span>
      {type === 'milestone' && data.target_date && (
        <span className="item-date">{new Date(data.target_date).toLocaleDateString()}</span>
      )}
      {type === 'task' && data.due_date && (
        <span className="item-date">{new Date(data.due_date).toLocaleDateString()}</span>
      )}
    </div>
  )
}

// Droppable calendar day cell
function DroppableDay({ day, date, isToday, hasEvents, children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: `day-${date.toISOString().split('T')[0]}`,
    data: { date }
  })

  return (
    <div
      ref={setNodeRef}
      className={`calendar-day ${isToday ? 'today' : ''} ${hasEvents ? 'has-events' : ''} ${isOver ? 'drop-target' : ''}`}
    >
      <div className="day-number">{day}</div>
      {children}
    </div>
  )
}

function CalendarView({ events, onReload, projectPlans = [], projects = [] }) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState('month') // month, week, day
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [expandedPlans, setExpandedPlans] = useState({})
  const [activeId, setActiveId] = useState(null)
  const [activeDragData, setActiveDragData] = useState(null)
  const [overDayId, setOverDayId] = useState(null)
  const [scheduling, setScheduling] = useState(false)
  const toast = useToast()

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    return { daysInMonth, startingDayOfWeek, year, month }
  }

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0]
    return events.filter(event => {
      const eventDate = new Date(event.start_time).toISOString().split('T')[0]
      return eventDate === dateStr
    })
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
  }

  const handleToday = () => {
    setCurrentDate(new Date())
  }

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Delete this event?')) {
      try {
        await calendarApi.delete(eventId)
        onReload()
      } catch (error) {
        console.error('Error deleting event:', error)
        alert('Error deleting event')
      }
    }
  }

  const togglePlanExpanded = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }))
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
    setActiveDragData(event.active.data.current)
  }

  const handleDragOver = (event) => {
    const overId = event.over?.id
    if (overId && String(overId).startsWith('day-')) {
      setOverDayId(overId)
    } else {
      setOverDayId(null)
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveId(null)
    setActiveDragData(null)
    setOverDayId(null)

    if (!over || !String(over.id).startsWith('day-')) {
      return
    }

    const dragData = active.data.current
    const dropDate = over.data.current?.date

    if (!dragData || !dropDate) {
      return
    }

    const dateStr = dropDate.toISOString().split('T')[0]
    setScheduling(true)

    try {
      if (dragData.type === 'milestone') {
        // Use existing createFromMilestone API
        await calendarApi.createFromMilestone({
          plan_id: dragData.plan_id,
          milestone_id: dragData.id,
          event_date: dateStr,
          event_time: '09:00',
          duration_hours: 1
        })
        toast.success(`Scheduled "${dragData.title}" for ${new Date(dateStr).toLocaleDateString()}`)
      } else if (dragData.type === 'task') {
        // Use createFromTask API
        await calendarApi.createFromTask({
          plan_id: dragData.plan_id,
          task_id: dragData.id,
          event_date: dateStr,
          event_time: '09:00',
          duration_hours: 1
        })
        toast.success(`Scheduled "${dragData.title}" for ${new Date(dateStr).toLocaleDateString()}`)
      }
      onReload()
    } catch (error) {
      console.error('Error scheduling item:', error)
      toast.error(error.response?.data?.detail || 'Failed to schedule item')
    } finally {
      setScheduling(false)
    }
  }

  const handleDragCancel = () => {
    setActiveId(null)
    setActiveDragData(null)
    setOverDayId(null)
  }

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const days = []
  // Add empty cells for days before the first of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null)
  }
  // Add cells for each day of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i)
  }

  // Get incomplete milestones and tasks for the sidebar
  const getPlansWithItems = () => {
    return projectPlans.map(plan => {
      const project = projects.find(p => p.id === plan.project_id)
      const incompleteMilestones = (plan.milestones || []).filter(m => !m.completed)
      const incompleteTasks = (plan.tasks || []).filter(t => t.status !== 'completed')
      return {
        ...plan,
        projectTitle: project?.title,
        incompleteMilestones,
        incompleteTasks
      }
    }).filter(plan => plan.incompleteMilestones.length > 0 || plan.incompleteTasks.length > 0)
  }

  const plansWithItems = getPlansWithItems()

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={`calendar-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Plans Sidebar */}
        <div className={`calendar-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <h3>Project Plans</h3>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <ChevronLeftIcon size={18} />
            </button>
          </div>

          {sidebarOpen && (
            <div className="sidebar-content">
              {plansWithItems.length === 0 ? (
                <div className="sidebar-empty">
                  <CalendarIcon size={24} />
                  <p>No pending items to schedule</p>
                </div>
              ) : (
                <div className="plans-list">
                  {plansWithItems.map(plan => (
                    <div key={plan.id} className="plan-item">
                      <div
                        className="plan-header"
                        onClick={() => togglePlanExpanded(plan.id)}
                      >
                        <ChevronDownIcon
                          size={16}
                          style={{
                            transform: expandedPlans[plan.id] ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.2s'
                          }}
                        />
                        <div className="plan-info">
                          <span className="plan-name">{plan.title}</span>
                          {plan.projectTitle && (
                            <span className="plan-project">{plan.projectTitle}</span>
                          )}
                        </div>
                        <span className="plan-count">
                          {plan.incompleteMilestones.length + plan.incompleteTasks.length}
                        </span>
                      </div>

                      {expandedPlans[plan.id] && (
                        <div className="plan-items">
                          {plan.incompleteMilestones.length > 0 && (
                            <div className="items-section">
                              <div className="items-label">Milestones</div>
                              {plan.incompleteMilestones.map(milestone => (
                                <DraggableItem
                                  key={milestone.id}
                                  id={milestone.id}
                                  type="milestone"
                                  data={{ ...milestone, plan_id: plan.id }}
                                  planTitle={plan.title}
                                />
                              ))}
                            </div>
                          )}
                          {plan.incompleteTasks.length > 0 && (
                            <div className="items-section">
                              <div className="items-label">Tasks</div>
                              {plan.incompleteTasks.map(task => (
                                <DraggableItem
                                  key={task.id}
                                  id={task.id}
                                  type="task"
                                  data={{ ...task, plan_id: plan.id }}
                                  planTitle={plan.title}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="sidebar-hint">
                Drag items to calendar days to schedule them
              </div>
            </div>
          )}

          {!sidebarOpen && (
            <button
              className="sidebar-expand-btn"
              onClick={() => setSidebarOpen(true)}
              title="Show project plans"
            >
              <ChevronRightIcon size={18} />
            </button>
          )}
        </div>

        {/* Calendar */}
        <div className="calendar-container">
          <div className="calendar-header">
            <div className="calendar-nav">
              <button className="btn btn-secondary" onClick={handlePrevMonth}>
                <ChevronLeftIcon size={18} />
              </button>
              <h2>{monthNames[month]} {year}</h2>
              <button className="btn btn-secondary" onClick={handleNextMonth}>
                <ChevronRightIcon size={18} />
              </button>
            </div>
            <button className="btn btn-primary" onClick={handleToday}>
              Today
            </button>
          </div>

          <div className="calendar-grid">
            <div className="calendar-weekdays">
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>

            <div className="calendar-days">
              {days.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} className="calendar-day empty" />
                }

                const date = new Date(year, month, day)
                const dayEvents = getEventsForDate(date)
                const isToday = date.toDateString() === new Date().toDateString()
                const dayId = `day-${date.toISOString().split('T')[0]}`
                const isOver = overDayId === dayId

                return (
                  <DroppableDay
                    key={day}
                    day={day}
                    date={date}
                    isToday={isToday}
                    hasEvents={dayEvents.length > 0}
                    isOver={isOver}
                  >
                    <div className="day-events">
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          className="calendar-event"
                          style={{
                            backgroundColor: event.color || '#3b82f6',
                            borderLeft: `3px solid ${event.color || '#2563eb'}`
                          }}
                          onClick={() => {
                            if (window.confirm(`${event.title}\n\n${event.description || ''}\n\nDelete this event?`)) {
                              handleDeleteEvent(event.id)
                            }
                          }}
                          title={`${event.title}${event.description ? '\n' + event.description : ''}`}
                        >
                          <div className="event-time">
                            {new Date(event.start_time).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="event-title">{event.title}</div>
                        </div>
                      ))}
                    </div>
                  </DroppableDay>
                )
              })}
            </div>
          </div>

          {events.length === 0 && projectPlans.length === 0 && (
            <div className="empty-state">
              <p>No events scheduled yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeDragData && (
          <div className={`drag-overlay-item ${activeDragData.type}`}>
            <span>{activeDragData.title}</span>
          </div>
        )}
      </DragOverlay>

      {/* Scheduling indicator */}
      {scheduling && (
        <div className="scheduling-overlay">
          <div className="scheduling-spinner"></div>
          <span>Scheduling...</span>
        </div>
      )}
    </DndContext>
  )
}

export default CalendarView
